import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from "./NewsTicker";
import SocialLinks, { SOCIAL_FIELDS } from "./SocialLinks";

const FINANCIAL_SERVICE_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "description", label: "Description" },
  { key: "address", label: "Address", type: "textarea", placeholder: "N/A" },
  { key: "phone", label: "Contact phone", placeholder: "(469) 343-4228" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

export interface FinancialServiceEntry {
  name: string;
  description: string;
  address: string;
  phone: string;
  image: string;
  website?: string;
  // Optional longer SEO description shown only on the individual detail page.
  // The card grid keeps using `description` for the short blurb.
  detailDescription?: string;
}

// Every listing's public detail-page URL is generated automatically from its
// name (no manual page creation needed): /financial-services/<name-slug>
export function financialSlug(name: string) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export const FINANCIAL_SERVICE_ENTRIES: FinancialServiceEntry[] = [
  {
    name: "Blossom Aggarwal, Blossom Financial Services, Licensed Financial Professional",
    description: "",
    address: "N/A",
    phone: "(469) 343-4228",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783545038058-4zojz.jpg",
  },
  {
    name: "Asset Retirement Legacy LLC",
    description: "",
    address: "1000 W Eldorado Pkwy STE 800, Little Elm, Texas 75068",
    phone: "469-769-6056",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-07/6c6c8214bd4f.jpeg",
  },
  {
    name: "Sudhir Pai, Managing Principal, My Time Equity, Legacy Wealth Planners",
    description: "",
    address: "4512 Legacy Dr Ste 100, Plano, TX 75024",
    phone: "(469) 702-1272",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783619718883-t7bae9.png",
  },
  {
    name: "JM Consultancy, Inc",
    description: "Licensed Financial Professional under JM Consultancy, Inc",
    address: "Dallas, TX",
    phone: "757-735-2853",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-09/f5465d4b18f7.jpeg",
  },
  {
    name: "Shreeji Financials",
    description: "",
    detailDescription:
      "Will and Trust Estate Planning Tax Free Retirement College Education Planning IRA / 401K Rollover Long Term Care Planning Join the World Class Business Opportunity",
    address: "N/A",
    phone: "281-891-0777",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-10-22/c54966de2cbc.jpeg",
    website: "https://hihello.com/p/d12ee77d-9988-4562-bb16-1c40a2d76b9e",
  },
];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop";

export function FinancialServiceCard({ entry, index }: { entry: FinancialServiceEntry; index: number }) {
  const telHref = entry.phone ? `tel:${entry.phone.replace(/[^\d+]/g, "")}` : "";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-md border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col w-full"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <Link to={`/financial-services/${financialSlug(entry.name)}`} className="flex flex-col flex-1">
      <div className="w-full aspect-[4/3] relative overflow-hidden bg-gray-100">
        <img
          src={entry.image || FALLBACK_IMAGE}
          alt={entry.name}
          className="w-full h-full object-contain"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="p-2 pb-0.5">
        {(() => {
          const parts = entry.name.split(",").map((p) => p.trim()).filter(Boolean);
          if (parts.length >= 3) {
            return (
              <h3 className="leading-tight">
                <span className="block text-[11px] font-bold text-gray-900">{parts[0]}</span>
                <span className="block text-[10px] font-bold text-[#082f6d] mt-0.5">{parts[1]}</span>
                <span className="block text-[9px] font-semibold text-gray-500 mt-0.5">{parts.slice(2).join(", ")}</span>
              </h3>
            );
          }
          return (
            <h3 className="text-[11px] font-bold text-gray-900 leading-tight">
              {entry.name}
            </h3>
          );
        })()}
        {entry.description && (
          <p className="text-[9px] text-gray-500 font-medium leading-snug mt-0.5">
            {entry.description}
          </p>
        )}
        {entry.address && entry.address !== "N/A" && (
          <p className="text-[9px] text-gray-500 font-medium leading-snug mt-0.5">
            {entry.address}
          </p>
        )}
      </div>
      </Link>
      <div className="p-2 pt-0.5 flex flex-col gap-1">
        <SocialLinks item={{ ...(entry as any), instagram: undefined, instagramUrl: undefined, instagram_url: undefined }} px={15} variant="sleek" />
        {telHref ? (
          <a
            href={telHref}
            className="w-full flex items-center justify-center gap-1 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[10px] font-semibold py-1 rounded-sm transition-colors duration-200 cursor-pointer"
          >
            <Phone className="w-2.5 h-2.5" /> Call
          </a>
        ) : (
          <span className="w-full flex items-center justify-center gap-1 bg-gray-50 border border-gray-200 text-gray-400 text-[10px] font-semibold py-1 rounded-sm cursor-not-allowed">
            Call
          </span>
        )}
      </div>
    </motion.div>
  );
}

export default function FinancialServices() {
  const [query, setQuery] = useState("");
  const mc = useManagedCollection<any>(
    "financial-services",
    FINANCIAL_SERVICE_ENTRIES,
    (e, i) => slugify(e.name || String(i))
  );

  const filtered = mc.items.filter(
    (e) =>
      query.trim() === "" ||
      e.name.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Financial Services"
        placeholder="Search financial services…"
        backgroundImage="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1600&h=400&fit=crop"
        query={query}
        setQuery={setQuery}
      />
      <NewsTicker />

      <section className="bg-gray-50/40 py-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">
              Financial Services in Dallas–DFW
            </h2>
            <p className="text-[13px] text-gray-500 mb-5">
              Find South Asian financial advisors, tax preparers, accountants, and insurance agents serving the community in Dallas–Fort Worth.
            </p>
            <AdminAddBar mc={mc} fields={FINANCIAL_SERVICE_FIELDS} label="Add Financial Service" />
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 items-start">
                {filtered.map((e, i) => (
                  <AdminItem key={e._key} mc={mc} item={e} fields={FINANCIAL_SERVICE_FIELDS} title="Financial Service">
                    <FinancialServiceCard entry={e as any} index={i} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No financial services match your search.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
